import tuningData from '../../../data/cpu-visual-polish.json';

const DEFENSIVE_LINE_ROLES = new Set(['DE1', 'DT1', 'DT2', 'DE2']);
const LINEBACKER_ROLES = new Set(['LB1', 'LB2', 'LB3']);
const AIRBORNE_BALL_STATES = new Set([
  'pass-flight',
  'punt-flight',
  'punt-bounce',
  'kick-flight',
  'kick-bounce',
  'onside-chip',
  'kick-blocked',
]);

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

function smoothstep(value: number): number {
  const clamped = clamp01(value);
  return clamped * clamped * (3 - 2 * clamped);
}

function roleHash(role: string): number {
  let hash = 0;
  for (let index = 0; index < role.length; index += 1) {
    hash = (hash * 33 + role.charCodeAt(index)) % 997;
  }
  return hash;
}

function snapFrameFor(frame: any): number | null {
  const replay = frame?.play?.arcadeReplay;
  const keyEventFrame = Number(replay?.keyEvents?.snap?.simFrame);
  if (Number.isFinite(keyEventFrame)) {
    return keyEventFrame;
  }

  const snapEvent = replay?.events?.find((event: any) => (
    event?.type === 'snap_received' || event?.type === 'snap'
  ));
  return Number.isFinite(Number(snapEvent?.simFrame)) ? Number(snapEvent.simFrame) : null;
}

export function defensiveReactionDelayFrames(player: any): number {
  const tuning = tuningData.reactionStagger;
  const role = String(player?.role || '');
  const assignment = String(player?.arcadeAssignment || '');

  if (/blitz|green-dog|rush-lane|edge-rush|interior-rush/i.test(assignment)) {
    return tuning.blitzFrames;
  }
  if (DEFENSIVE_LINE_ROLES.has(role)) {
    return tuning.defensiveLineFrames;
  }
  if (LINEBACKER_ROLES.has(role)) {
    return tuning.linebackerFrames;
  }
  if (role.startsWith('CB')) {
    return tuning.cornerFrames;
  }
  if (role.startsWith('S')) {
    return tuning.safetyFrames;
  }
  return tuning.linebackerFrames;
}

/**
 * Applies a tiny render-only hold against the recorded post-snap movement.
 * The authoritative replay frame and its hitboxes remain untouched.
 */
export function applyDefensiveReactionStagger(
  players: any[] = [],
  formationPlayers: any[] = [],
  frame: any,
): any[] {
  const tuning = tuningData.reactionStagger;
  const snapFrame = snapFrameFor(frame);
  const simFrame = Number(frame?.simFrame);
  if (!tuning.enabled || frame?.scenePhase !== 'live' || snapFrame === null || !Number.isFinite(simFrame)) {
    return players;
  }

  const elapsedFrames = simFrame - snapFrame;
  if (elapsedFrames < 0) {
    return players;
  }

  const formationByRole = new Map(formationPlayers.map((player: any) => [player?.role, player]));
  players.forEach((player: any) => {
    const delayFrames = defensiveReactionDelayFrames(player);
    const formationPlayer: any = formationByRole.get(player?.role);
    if (
      delayFrames <= 0
      || elapsedFrames > delayFrames + 1
      || !player?.moving
      || player?.down
      || player?.tackled
      || !formationPlayer
    ) {
      return;
    }

    const deltaX = Number(formationPlayer.x) - Number(player.x);
    const deltaY = Number(formationPlayer.y) - Number(player.y);
    if (![deltaX, deltaY].every(Number.isFinite)) {
      return;
    }

    const distance = Math.hypot(deltaX, deltaY);
    if (distance < 0.01) {
      return;
    }

    const progress = smoothstep(elapsedFrames / Math.max(1, delayFrames + 1));
    const heldDistance = Math.min(distance, tuning.maximumHoldPixels) * (1 - progress);
    player.x += (deltaX / distance) * heldDistance;
    player.y += (deltaY / distance) * heldDistance;
    player.arcadeVisualReactionDelay = delayFrames;
  });

  return players;
}

function pursuitCandidates(players: any[], carrier: any): Array<{ player: any; distance: number }> {
  const tuning = tuningData.pursuitLanes;
  return players
    .filter((player: any) => (
      player?.moving
      && !player?.down
      && !player?.tackled
      && player?.role !== carrier?.role
      && !/block|engag|pass-protect|pocket-set/i.test(String(player?.arcadeAssignment || ''))
    ))
    .map((player: any) => ({
      player,
      distance: Math.hypot(Number(player.x) - Number(carrier.x), Number(player.y) - Number(carrier.y)),
    }))
    .filter(({ distance }) => (
      Number.isFinite(distance)
      && distance >= tuning.minimumCarrierDistancePixels
      && distance <= tuning.maximumCarrierDistancePixels
    ))
    .sort((left, right) => left.distance - right.distance)
    .slice(0, tuning.maximumPursuers);
}

/** Spreads secondary pursuit lanes while leaving the nearest finisher alone. */
export function applyPursuitLaneSpacing(players: any[] = [], carrier: any): any[] {
  const tuning = tuningData.pursuitLanes;
  if (!tuning.enabled || !carrier || !Number.isFinite(carrier.x) || !Number.isFinite(carrier.y)) {
    return players;
  }

  pursuitCandidates(players, carrier).forEach(({ player }, index) => {
    if (index === 0) {
      player.arcadeVisualPursuitLane = 0;
      return;
    }

    const lateralDistance = Math.abs(Number(player.y) - Number(carrier.y));
    const crowdFactor = clamp01(1 - lateralDistance / tuning.crowdWidthPixels);
    if (crowdFactor <= 0) {
      return;
    }

    const naturalSide = Math.sign(Number(player.y) - Number(carrier.y));
    const laneSide = naturalSide || (roleHash(String(player.role || index)) % 2 === 0 ? -1 : 1);
    const offset = laneSide * Math.min(
      tuning.maximumOffsetPixels,
      tuning.spacingPixels * index,
    ) * crowdFactor;
    player.y += offset;
    player.arcadeVisualPursuitLane = index;
  });

  return players;
}

export type AirborneBallShadow = {
  centerX: number;
  groundY: number;
  halfWidth: number;
  height: number;
  alpha: number;
};

export type FumbleImpactCue = {
  fumblerRole: string;
  forcedByRole: string | null;
  intensity: number;
  progress: number;
};

export function usesAirborneBallPresentation(ball: any): boolean {
  return AIRBORNE_BALL_STATES.has(String(ball?.arcadeState || ''));
}

export function resolveFumbleImpactCue(frame: any): FumbleImpactCue | null {
  const replay = frame?.play?.arcadeReplay;
  const event = replay?.keyEvents?.fumble
    || replay?.events?.find((candidate: any) => candidate?.type === 'fumble_forced')
    || null;
  const eventFrame = Number(event?.simFrame);
  const currentFrame = Number(frame?.simFrame);
  const windowFrames = tuningData.fumbleBurst.windowFrames;
  if (!Number.isFinite(eventFrame) || !Number.isFinite(currentFrame)) {
    return null;
  }
  const elapsed = currentFrame - eventFrame;
  if (elapsed < 0 || elapsed > windowFrames) {
    return null;
  }

  const fumblerRole = String(
    event?.payload?.carrierRole
      || replay?.participants?.fumblerRole
      || frame?.play?.participants?.fumblerRole
      || '',
  );
  if (!fumblerRole) {
    return null;
  }
  const progress = clamp01(elapsed / Math.max(1, windowFrames));
  return {
    fumblerRole,
    forcedByRole: String(
      event?.payload?.forcedByRole
        || replay?.participants?.forcedByRole
        || frame?.play?.participants?.forcedByRole
        || '',
    ) || null,
    intensity: 1 - smoothstep(progress),
    progress,
  };
}

export function resolveAirborneBallShadow(ball: any): AirborneBallShadow | null {
  const tuning = tuningData.airborneBallShadow;
  const height = Number(ball?.arcadeHeight);
  if (
    !usesAirborneBallPresentation(ball)
    || !Number.isFinite(height)
    || height < tuning.minimumHeight
    || !Number.isFinite(Number(ball?.ballX))
    || !Number.isFinite(Number(ball?.ballY))
  ) {
    return null;
  }

  const heightRatio = clamp01(height / 4);
  const recordedGroundY = Number(ball?.arcadeGroundY);
  return {
    centerX: Math.round(Number(ball.ballX) + 4),
    groundY: Number.isFinite(recordedGroundY)
      ? Math.round(recordedGroundY)
      : Math.round(Number(ball.ballY) + height * tuning.heightToPixels + 4),
    halfWidth: Math.round(clamp(
      tuning.minimumHalfWidthPixels + heightRatio * 2,
      tuning.minimumHalfWidthPixels,
      tuning.maximumHalfWidthPixels,
    )),
    height: tuning.heightPixels,
    alpha: tuning.maximumAlpha
      - (tuning.maximumAlpha - tuning.minimumAlpha) * heightRatio,
  };
}

export const cpuVisualPolishTuning = tuningData;
